import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import Auth from '../../../api/auth';
import { FormModal } from '../../../components/Formik';
import useApiQuery from '../../../hooks/useApiQuery';

import type { BusinessUnit } from '@/types/businessUnitTypes';

interface User {
  _id: string;
  email: string;
  activated: boolean;
}

interface BusinessUnitFormValues extends Record<string, unknown> {
  name: string;
  description: string;
  businessUnitManager: string | null;
}

interface CreateBusinessUnitModalProps {
  onSubmit: (data: BusinessUnit) => void;
  onCancel: () => void;
}

interface RouteParams extends Record<string, string | undefined> {
  organizationId: string;
}

const CreateBusinessUnitFormModal = ({ onSubmit, onCancel }: CreateBusinessUnitModalProps) => {
  const { organizationId } = useParams<RouteParams>();
  const [businessUnitManagers, setBusinessUnitManagers] = useState<User[]>([]);

  const { makeApiCall: fetchBusinessUnitManagers } = useApiQuery(Auth.fetchV2Users);
  const { makeApiCall: createBusinessUnitCall } = useApiQuery(
    Auth.createV1BusinessUnit as (...args: unknown[]) => Promise<unknown>,
    {
      successMessage: 'businessUnitCreation',
    }
  );

  useEffect(() => {
    const getBuManagers = async () => {
      const data = (await fetchBusinessUnitManagers({
        organizationId,
        roles: 'BUSINESS_UNIT_MANAGER',
      })) as User[];

      const activatedBuManagers = data.filter((buManager: User) => buManager.activated);
      setBusinessUnitManagers(activatedBuManagers);
    };

    getBuManagers();
  }, [fetchBusinessUnitManagers, organizationId]);

  const fields = [
    {
      fieldType: 'text',
      name: 'name',
      label: 'Name',
    },
    {
      fieldType: 'text',
      name: 'description',
      label: 'Description',
    },
    {
      fieldType: 'select',
      name: 'businessUnitManager',
      label: 'Business Unit Manager',
      menuItems: businessUnitManagers.map(({ _id, email }) => ({ value: _id, label: email })),
    },
  ];

  const initialValues: BusinessUnitFormValues = {
    name: '',
    description: '',
    businessUnitManager: null,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Required'),
    description: Yup.string().required('Required'),
  });

  const createBusinessUnit = async (formValues: Record<string, unknown>): Promise<void> => {
    onCancel();

    try {
      const data = await createBusinessUnitCall({
        ...formValues,
        organizationId,
      });

      onSubmit(data as BusinessUnit);
    } catch (error) {
      console.error('Error creating business unit:', error);
      return;
    }
  };

  return (
    <FormModal
      title='Create New Business Unit'
      fields={fields}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        createBusinessUnit(values);
      }}
      onCancel={onCancel}
      submitLabel='Create'
    />
  );
};

export default CreateBusinessUnitFormModal;
